/**
 * BlueprintExamples - Showcases all three research blueprints on the landing page
 *
 * Renders static, curated examples for General, Financial, and Strategic research modes
 * using the actual visualization components (LogicTree, FinancialLogicTree, StrategicLogicTree)
 * with pre-built "concluded" state data.
 */

import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Search, TrendingUp, Target } from 'lucide-react';
import { LogicTree } from './LogicTree';
import { FinancialLogicTree } from './FinancialLogicTree';
import { StrategicLogicTree } from './StrategicLogicTree';
import { buildGeneralExampleSession } from './exampleData/generalExample';
import { buildFinancialExampleState } from './exampleData/financialExample';
import { buildStrategicExampleState } from './exampleData/strategicExample';
import { SectionLabel } from './SectionLabel';

type BlueprintTab = 'general' | 'financial' | 'strategic';

const TAB_CONFIG: Array<{
  id: BlueprintTab;
  label: string;
  shortLabel: string;
  icon: typeof Search;
  description: string;
  query: string;
  color: string;
  borderColor: string;
  bgColor: string;
  isAlpha?: boolean;
}> = [
  {
    id: 'general',
    label: 'General Research',
    shortLabel: 'General',
    icon: Search,
    description: 'Multi-agent investigation with fact-checking and debate',
    query: 'What are the long-term health effects of intermittent fasting?',
    color: 'text-emerald-600 dark:text-emerald-400',
    borderColor: 'border-emerald-500/30',
    bgColor: 'bg-emerald-500/10',
  },
  {
    id: 'financial',
    label: 'Financial Deep Dive',
    shortLabel: 'Financial',
    icon: TrendingUp,
    description: 'Bull vs Bear adversarial analysis with Kill Sheet',
    query: 'Is NVIDIA (NVDA) overvalued at current levels?',
    color: 'text-amber-600 dark:text-amber-400',
    borderColor: 'border-amber-500/30',
    bgColor: 'bg-amber-500/10',
  },
  {
    id: 'strategic',
    label: 'Strategic Analysis',
    shortLabel: 'Strategic',
    icon: Target,
    description: 'MECE issue decomposition with Ghost Deck output',
    query: 'Should a mid-size B2B SaaS company expand into the European market?',
    color: 'text-blue-600 dark:text-blue-400',
    borderColor: 'border-blue-500/30',
    bgColor: 'bg-blue-500/10',
    isAlpha: true,
  },
];

export function BlueprintExamples() {
  const [activeTab, setActiveTab] = useState<BlueprintTab>('general');

  // Build example data (memoized to avoid recreation on re-renders)
  const generalSession = useMemo(() => buildGeneralExampleSession(), []);
  const financialState = useMemo(() => buildFinancialExampleState(), []);
  const strategicState = useMemo(() => buildStrategicExampleState(), []);

  const activeConfig = TAB_CONFIG.find(t => t.id === activeTab)!;

  return (
    <section className="relative py-20 sm:py-28">
      <div className="verdict-divider mb-20 sm:mb-28" />
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-12 sm:mb-16">
          <SectionLabel className="mb-4">BLUEPRINT SHOWCASE</SectionLabel>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-5">
            Three Research <span className="text-verdict-primary">Blueprints</span>, One Platform
          </h2>
          <p className="text-text-secondary text-sm sm:text-base max-w-2xl mx-auto">
            Each blueprint deploys a specialized methodology with dedicated agent roles,
            structured debate, and purpose-built deliverables.
          </p>
        </div>

        {/* Tab selector */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex gap-2 p-1.5 bg-surface/80 border border-border-subtle rounded-xl backdrop-blur-sm">
            {TAB_CONFIG.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                    isActive
                      ? `${tab.bgColor} ${tab.color} ${tab.borderColor} border shadow-lg`
                      : 'text-text-secondary hover:text-text-primary hover:bg-surface-light border border-transparent'
                  )}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.shortLabel}</span>
                  {tab.isAlpha && (
                    <sup className="text-[7px] font-bold uppercase tracking-wider text-amber-400/80 -top-2 relative ml-0.5 select-none">
                      alpha
                    </sup>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Active tab description */}
        <div className="text-center mb-6">
          <p className={cn('text-sm font-medium', activeConfig.color)}>{activeConfig.description}</p>
          <p className="text-xs text-text-muted mt-1.5 max-w-lg mx-auto">
            Example query: <span className="text-text-secondary italic">&ldquo;{activeConfig.query}&rdquo;</span>
          </p>
        </div>

        {/* Blueprint visualization */}
        <div className="relative">
          {/* Subtle gradient border */}
          <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-white/10 via-white/5 to-transparent pointer-events-none" />

          <div className="relative rounded-2xl overflow-hidden h-[700px] lg:h-[850px]">
            {activeTab === 'general' && (
              <LogicTree
                session={generalSession}
                className="w-full max-w-none !h-full !max-h-full"
                hideActions
              />
            )}
            {activeTab === 'financial' && (
              <FinancialLogicTree
                state={financialState}
                query="Is NVIDIA (NVDA) overvalued at current levels?"
                className="w-full max-w-none !h-full !max-h-full"
                hideActions
              />
            )}
            {activeTab === 'strategic' && (
              <StrategicLogicTree
                state={strategicState}
                query="Should a mid-size B2B SaaS company expand into the European market?"
                className="w-full max-w-none !h-full !max-h-full"
                hideActions
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

